import Dexie, { type Table } from "dexie";
import { v4 as uuid } from "uuid";
import {
	AutoSyncConfig,
	FieldContinuationDataForResolver,
	FieldInstanceForResolver,
	FieldTemplateForResolver,
	FormGroupForResolver,
	FormInstanceForResolver,
	FormTemplateForResolver,
	Image,
	ImageBinary,
	Option,
	Permission,
	ProjectConfig,
	ProjectForResolver,
	Revision,
	RoleForResolver,
	UserForResolver,
	AnalyticsEventForResolver,
} from "./definitions";

// export const db = new Dexie("record");

// https://dexie.org/
// https://dexie.org/docs/API-Reference#quick-reference <-- Super fucking helpful for everything from index types to migrating schemas to queries
// https://dexie.org/docs/Tutorial/Design#database-versioning <-- Has good schema stuff, as well as examples on using where, and, and or clauses
// https://dexie.org/docs/Version/Version.stores() <-- reiterates some warnings I outline below

// Okay, time to explain some shit. If you don't know what you're doing here, or have never worked with Dexie, please read this
// First, having the db called like this in one file means we're never re-instantiating it. The db is created once, which was a huge performance thing we ran into in Explore/Curate.
// Next, version. VERSION IS VERY IMPORTANT. Why? We set our schema here (more on that in a bit), but what if we change it? Dexie creates the database and remembers the version.
// Let's say an archaeologist takes this out to the field, and while they're out there, we decide to update the schema. That changes the entire logic of the database, because a
// table is different. When they come back online, dexie notices that shit's outdated because of the version number, and updates their configuration. Every time we update the schema,
// we have to update the version number. Sometimes though, changing the schema would completely fuck the database as a whole. Look at this example, we can supply a migration script
// to keep everything compatible and awesome. https://dexie.org/docs/Tutorial/Design#database-versioning
// Speaking of the schema, let's talk schema. THE SCHEMA DOESN'T LOOK LIKE A TYPICAL SQL/FIREBASE/GRAPHQL/MONGO MODEL. The ONLY properties we put in here are the properties we want
// indexed (things we want to use where clauses on, or query by). This means a few things, namely that a document in a table isn't forced to adhere to a set scheme. We have to
// manage that. Unlike with AWS, we have limited space. The way indexes work, they eat up space in memory, so we don't want to be indexing huge strings, images, anything like that.
// We should really only index what needs indexed. The rule of thumb, as stated in their docs, is if you want to use a where clause on a property, index it.

// db.version(4).stores({
// 	formTemplate: "id",
// 	fieldTemplate: "id, formTemplate, tableField",// get all field templates WHERE formTemplateId === ourFormTemplateId (the id from the formTemplateTable)
// 	option: "id, fieldTemplateId, formTemplate",// get all options WHERE fieldTemplateId === ourFieldTemplateId
// 	formGroup: "id, projectId",// get all formGroups WHERE projectId === ourProjectId
// 	formInstance: "id, formGroup",// get all formInstances WHERE formGroupId === ourFormGroupId
// 	fieldInstance: "id, formInstance, tableField, formGroup, altInstanceId, touched, [formGroup+touched]",// get all fieldInstances WHERE formInstanceId === ourFormInstanceId
// 	analytics: "id, userDoc",// get all analytics WHERE userDoc === ourUserId
// 	user: "id, username",
// 	project: "id",
// 	image: "id, key",
// 	imageBinary: "key",
// 	fieldContinuationData: "id"
// });

export class RecordOfflineDexie extends Dexie {
	// 'friends' is added by dexie when declaring the stores()
	// We just tell the typing system this is the case
	formTemplate!: Table<FormTemplateForResolver>;
	fieldTemplate!: Table<FieldTemplateForResolver>;
	option!: Table<Option>;
	formGroup!: Table<FormGroupForResolver>;
	formInstance!: Table<FormInstanceForResolver>;
	fieldInstance!: Table<FieldInstanceForResolver>;
	revision!: Table<Revision>;
	fieldContinuationData!: Table<FieldContinuationDataForResolver>;
	user!: Table<UserForResolver>;
	role!: Table<RoleForResolver>;
	permission!: Table<Permission>;
	project!: Table<ProjectForResolver>;
	image!: Table<Image>;
	imageBinary!: Table<ImageBinary>;
	autoSyncConfig!: Table<AutoSyncConfig>;
	projectConfig!: Table<ProjectConfig>;
	analyticsEvent!: Table<AnalyticsEventForResolver>;

	constructor() {
		super("record");
		this.version(5).stores({
			formTemplate: "id",
			fieldTemplate: "id, formTemplate, tableField", // get all field templates WHERE formTemplateId === ourFormTemplateId (the id from the formTemplateTable)
			option: "id, fieldTemplateId, formTemplate", // get all options WHERE fieldTemplateId === ourFieldTemplateId
			formGroup: "id, projectId", // get all formGroups WHERE projectId === ourProjectId
			formInstance: "id, formGroup", // get all formInstances WHERE formGroupId === ourFormGroupId
			fieldInstance: "id, formInstance, tableField, formGroup, altInstanceId, touched, [formGroup+touched]", // get all fieldInstances WHERE formInstanceId === ourFormInstanceId
			analytics: "id, userDoc", // get all analytics WHERE userDoc === ourUserId
			user: "id, username",
			project: "id",
			image: "id, key",
			imageBinary: "key",
			fieldContinuationData: "id, formTemplateId, fieldTemplateId",
		});
		this.version(6)
			.stores({
				formTemplate: "id",
				fieldTemplate: "id, formTemplate, tableField", // get all field templates WHERE formTemplateId === ourFormTemplateId (the id from the formTemplateTable)
				option: "id, fieldTemplateId, formTemplate", // get all options WHERE fieldTemplateId === ourFieldTemplateId
				formGroup: "id, projectId", // get all formGroups WHERE projectId === ourProjectId
				formInstance: "id, formGroup", // get all formInstances WHERE formGroupId === ourFormGroupId
				fieldInstance: "id, formInstance, tableField, formGroup, altInstanceId, touched, [formGroup+touched]", // get all fieldInstances WHERE formInstanceId === ourFormInstanceId
				analytics: "id, userDoc", // get all analytics WHERE userDoc === ourUserId
				user: "id, username",
				project: "id",
				image: "id, key",
				imageBinary: "key",
				fieldContinuationData: "id, formTemplateId, fieldTemplateId",
				autoSyncConfig: "id, baseTemplateId",
				projectConfig: "id, projectId",
			})
			.upgrade(async (tx) => {
				const templateMap = new Map();

				// Step 1. get and map out field templates of the comma value (picklist) type.
				// we actually don have to care about radios.
				await tx
					.table("fieldTemplate")
					.toCollection()
					.each((fieldTemplate) => {
						if (fieldTemplate.inputType === "checkbox" || fieldTemplate.inputType === "radio" || fieldTemplate.inputType === "dropdown") {
							templateMap.set(fieldTemplate.id, fieldTemplate);
						}
					});
				// 2. Next up get all the field instances, then check if their field template Id has been added
				// to the map created in step 1. If it has, then replace all commas with char code 31 in the value
				// and the revisions.
				await tx
					.table("fieldInstance")
					.toCollection()
					.modify((fieldInstance) => {
						if (templateMap.has(fieldInstance.fieldTemplateId)) {
							// it is a picklist so value needs commas replaced by String.fromCharCode(31)
							const newValue = fieldInstance.value.replaceAll(",", String.fromCharCode(31));
							fieldInstance.value = newValue;
							let newRevisions = [];
							// gotta do revisions
							for (let i = 0; i < fieldInstance.revisions.length; i++) {
								const revisionElement = fieldInstance.revisions[i];
								const newRevisionElement = revisionElement.replaceAll(",", String.fromCharCode(31));
								newRevisions.push(newRevisionElement);
							}
							fieldInstance.revisions = newRevisions;
						}
					});
				// 3. Clear all the templates from the DB.  We are only doing this because we've been forcing
				// people to download all the templates all the time.
				await tx.table("fieldTemplate").clear();
				await tx.table("option").clear();
				await tx.table("fieldContinuationData").clear();
				return tx.table("formTemplate").clear();
			});
		this.version(7)
			.stores({
				formTemplate: "id",
				fieldTemplate: "id, formTemplate, tableField", // get all field templates WHERE formTemplateId === ourFormTemplateId (the id from the formTemplateTable)
				option: "id, fieldTemplateId, formTemplate", // get all options WHERE fieldTemplateId === ourFieldTemplateId
				formGroup: "id, projectId", // get all formGroups WHERE projectId === ourProjectId
				formInstance: "id, formGroup", // get all formInstances WHERE formGroupId === ourFormGroupId
				fieldInstance: "id, formInstance, tableField, formGroup, altInstanceId, touched, [formGroup+touched]", // get all fieldInstances WHERE formInstanceId === ourFormInstanceId
				analytics: "id, userDoc", // get all analytics WHERE userDoc === ourUserId
				user: "id, username",
				project: "id",
				image: "id, key",
				imageBinary: "key",
				fieldContinuationData: "id, formTemplateId, fieldTemplateId",
				autoSyncConfig: "id, baseTemplateId",
				projectConfig: "id, projectId",
				sentryOffline: "id, sentryEvent",
			})
			.upgrade(async (tx) => {
				// doin a big clear this time.
				await tx.table("fieldTemplate").clear();
				await tx.table("option").clear();
				await tx.table("fieldContinuationData").clear();
				return tx.table("formTemplate").clear();
			});
		this.version(8)
			.stores({
				formTemplate: "id",
				fieldTemplate: "id, formTemplate, tableField", // get all field templates WHERE formTemplateId === ourFormTemplateId (the id from the formTemplateTable)
				option: "id, fieldTemplateId, formTemplate", // get all options WHERE fieldTemplateId === ourFieldTemplateId
				formGroup: "id, projectId", // get all formGroups WHERE projectId === ourProjectId
				formInstance: "id, formGroup", // get all formInstances WHERE formGroupId === ourFormGroupId
				fieldInstance: "id, formInstance, tableField, formGroup, altInstanceId, touched, [formGroup+touched]", // get all fieldInstances WHERE formInstanceId === ourFormInstanceId
				analytics: "id, userDoc", // get all analytics WHERE userDoc === ourUserId
				user: "id, username",
				project: "id",
				image: "id, key",
				imageBinary: "key",
				fieldContinuationData: "id, formTemplateId, fieldTemplateId",
				autoSyncConfig: "id, baseTemplateId",
				projectConfig: "id, projectId",
				sentryOffline: "id, sentryEvent",
				revision: "id, fieldInstance, formGroup",
			})
			.upgrade(async (tx) => {
				const newRevisions: Revision[] = [];
				await tx
					.table("fieldInstance")
					.toCollection()
					.modify((fieldInstance) => {
						if (fieldInstance.revisions?.length > 0) {
							for (let i = 0; i < fieldInstance.revisions.length; i++) {
								const revisionString = fieldInstance.revisions[i];
								const splitRevision = revisionString.split(String.fromCharCode(31));
								const [value, author, timestamp] = splitRevision;
								if (!value || value === "") {
									// skipping because we dont need to keep the empty  for now
									continue;
								}

								const isoTimestamp = timestamp;
								const id = uuid();
								// Setup a new revision document for each entry.
								const newRevisionDoc = {
									id,
									value,
									author,
									formGroup: fieldInstance.formGroup,
									group: fieldInstance.group,
									fieldInstance: fieldInstance.id,
									createdAt: isoTimestamp,
									updatedAt: isoTimestamp,
								};

								newRevisions.push(newRevisionDoc);
							}
						}

						delete fieldInstance.revisions;
					});
				await db.revision.bulkPut(newRevisions);
			});
		this.version(9)
			.stores({
				formTemplate: "id",
				fieldTemplate: "id, formTemplate, tableField", // get all field templates WHERE formTemplateId === ourFormTemplateId (the id from the formTemplateTable)
				option: "id, fieldTemplateId, formTemplate", // get all options WHERE fieldTemplateId === ourFieldTemplateId
				formGroup: "id, projectId", // get all formGroups WHERE projectId === ourProjectId
				formInstance: "id, formGroup", // get all formInstances WHERE formGroupId === ourFormGroupId
				fieldInstance: "id, formInstance, tableField, formGroup, altInstanceId, touched, [formGroup+touched]", // get all fieldInstances WHERE formInstanceId === ourFormInstanceId
				analytics: "id, userDoc", // get all analytics WHERE userDoc === ourUserId
				user: "id, username",
				project: "id",
				image: "id, key, formGroup, project",
				imageBinary: "key",
				fieldContinuationData: "id, formTemplateId, fieldTemplateId",
				autoSyncConfig: "id, baseTemplateId",
				projectConfig: "id, projectId",
				sentryOffline: "id, sentryEvent",
				revision: "id, fieldInstance, formGroup",
			})
			.upgrade(async (tx) => {
				const formGroups: FormGroupForResolver[] = await tx.table("formGroup").toArray();
				const fieldInstancePromises: Promise<FieldInstanceForResolver[]>[] = [];
				for (const formGroup of formGroups) {
					fieldInstancePromises.push(tx.table("fieldInstance").where("formGroup").equals(formGroup.id).toArray());
				}
				// Should be n fieldInstance arrays, where n is the number of formGroups. We can then loop through the outer level, and
				// use that index on the formGroups array to get the formGroup id and the projectId off the formGroup itself. Oh, obviously
				// we should get the image first
				const fieldInstanceArrays = await Promise.all(fieldInstancePromises.map(async (promise) => await promise));

				const imagePromises: Promise<Image[]>[] = [];
				const arrayOfImageIds: string[][] = [];
				for (const fieldInstanceArray of fieldInstanceArrays) {
					const imageIds: string[] = [];
					for (const fieldInstance of fieldInstanceArray) {
						if (fieldInstance.imageId) imageIds.push(fieldInstance.imageId);
					}
					imagePromises.push(tx.table("image").bulkGet(imageIds));
					arrayOfImageIds.push(imageIds);
				}
				// Okay yeah remember that comment like 11 lines up? Now's the time. n formGroups => n fieldInstance arrays => n imageArrays
				const imageArrays = await Promise.all(imagePromises.map(async (promise) => await promise));
				const newImages: Image[] = [];
				for (let i = 0; i < imageArrays.length; i++) {
					for (let j = 0; j < imageArrays[i].length; j++) {
						const image = imageArrays[i][j];
						// If statement is needed because of the thing creating fields and no images in the photologs. Without this, the
						// script will fail in that case, and then people's shit will still be messed up
						if (image) {
							image.formGroup = formGroups[i].id;
							image.project = formGroups[i].projectId;
							newImages.push(image);
						} else {
							// create the image doc with the field's imageId
							const imageId = arrayOfImageIds[i][j];
							const newImage: Image = {
								id: imageId,
								key: "",
								formGroup: formGroups[i].id,
								project: formGroups[i].projectId,
								dpi: "",
								aspectX: "",
								aspectY: "",
								rotation: "",
								createdAt: new Date().toISOString(),
								// Setting it really early in case the image somehow got created online somewhere so we keep what they have
								updatedAt: new Date(2020, 0, 1).toISOString(),
								group: formGroups[i].group,
							};
							newImages.push(newImage);
						}
					}
				}
				await db.image.bulkPut(newImages);
			});
		this.version(10)
			.stores({
				formTemplate: "id",
				fieldTemplate: "id, formTemplate, tableField", // get all field templates WHERE formTemplateId === ourFormTemplateId (the id from the formTemplateTable)
				option: "id, fieldTemplateId, formTemplate", // get all options WHERE fieldTemplateId === ourFieldTemplateId
				formGroup: "id, projectId", // get all formGroups WHERE projectId === ourProjectId
				formInstance: "id, formGroup", // get all formInstances WHERE formGroupId === ourFormGroupId
				fieldInstance: "id, formInstance, tableField, formGroup, altInstanceId, touched, [formGroup+touched]", // get all fieldInstances WHERE formInstanceId === ourFormInstanceId
				analytics: "id, userDoc", // get all analytics WHERE userDoc === ourUserId
				user: "id, username",
				project: "id",
				image: "id, key, formGroup, project",
				imageBinary: "key",
				fieldContinuationData: "id, formTemplateId, fieldTemplateId, parentFormTemplateId",
				autoSyncConfig: "id, baseTemplateId",
				projectConfig: "id, projectId",
				sentryOffline: "id, sentryEvent",
				revision: "id, fieldInstance, formGroup",
			})
			.upgrade(async (tx) => {
				// Give fieldContinuationDatas their parent formTemplateId. Give all templatey docs and users a group array
				const [fieldTemplates, formTemplates, options] = await Promise.all([
					tx.table("fieldTemplate").toArray() as Promise<FieldTemplateForResolver[]>,
					tx.table("formTemplate").toArray() as Promise<FormTemplateForResolver[]>,
					tx.table("option").toArray() as Promise<Option[]>,
				]);
				const defaultTemplateGroup = "PERMISSION_DefaultTemplates";
				const contDataIds = [];
				const formTemplateIds = [];
				const finalFieldTemplates: FieldTemplateForResolver[] = [];
				const tableCells: FieldTemplateForResolver[] = [];
				const tableFieldToFormTemplate: Map<string, string> = new Map();
				// Get group on fieldTemplates, get list of cont datas
				for (const fieldTemplate of fieldTemplates) {
					if (fieldTemplate.continuationDataId) {
						contDataIds.push(fieldTemplate.continuationDataId);
						formTemplateIds.push(fieldTemplate.formTemplate);
					}
					if (fieldTemplate.tableField) {
						tableCells.push(fieldTemplate);
					} else {
						if (fieldTemplate.rowOrder && fieldTemplate.rowOrder.length > 0)
							tableFieldToFormTemplate.set(fieldTemplate.id, fieldTemplate.formTemplate);
						finalFieldTemplates.push(
							fieldTemplate.group === "FactorEarth" ? { ...fieldTemplate, readGroups: [defaultTemplateGroup] } : { ...fieldTemplate },
						);
					}
				}
				// Give cont data their parent formTemplate ids and fixes the group
				const contDatas: FieldContinuationDataForResolver[] = await tx.table("fieldContinuationData").bulkGet(contDataIds);
				const finalContDatas: FieldContinuationDataForResolver[] = [];
				for (let i = 0; i < contDatas.length; i++) {
					const contData = contDatas[i];
					const formTemplateId = formTemplateIds[i];
					if (!contData.parentFormTemplateId && formTemplateId) contData.parentFormTemplateId = formTemplateId;
					if (contData.group === "FactorEarth") contData.readGroups = [defaultTemplateGroup];
				}
				// Give all tableFields the correct formTemplateId
				for (const cell of tableCells) {
					if (cell.tableField) {
						// Check makes ts happy
						const formTemplate = tableFieldToFormTemplate.get(cell.tableField);
						if (formTemplate && cell.formTemplate !== formTemplate) cell.formTemplate = formTemplate;
						if (cell.group === "FactorEarth") cell.readGroups = [defaultTemplateGroup];
						finalFieldTemplates.push(cell);
					}
				}
				// Get groups on FormTemplates
				const finalFormTemplates = formTemplates.map((formTemplate) => {
					if (formTemplate.group === "FactorEarth") formTemplate.readGroups = [defaultTemplateGroup];
					return formTemplate;
				});
				// Get groups on Options
				const finalOptions = options.map((option) => {
					if (option.group === "FactorEarth") option.readGroups = [defaultTemplateGroup];
					return option;
				});
				await Promise.all([
					tx.table("formTemplate").bulkPut(finalFormTemplates),
					tx.table("fieldTemplate").bulkPut(finalFieldTemplates),
					tx.table("option").bulkPut(finalOptions),
					tx.table("fieldContinuationData").bulkPut(finalContDatas),
				]);
			});
		this.version(11)
			.stores({
				formTemplate: "id",
				fieldTemplate: "id, formTemplate, tableField", // get all field templates WHERE formTemplateId === ourFormTemplateId (the id from the formTemplateTable)
				option: "id, fieldTemplateId, formTemplate", // get all options WHERE fieldTemplateId === ourFieldTemplateId
				formGroup: "id, projectId", // get all formGroups WHERE projectId === ourProjectId
				formInstance: "id, formGroup", // get all formInstances WHERE formGroupId === ourFormGroupId
				fieldInstance: "id, formInstance, tableField, formGroup, altInstanceId, touched, [formGroup+touched]", // get all fieldInstances WHERE formInstanceId === ourFormInstanceId
				analytics: "id, userDoc", // get all analytics WHERE userDoc === ourUserId
				user: "id, username",
				project: "id",
				image: "id, key, formGroup, project",
				imageBinary: "key",
				fieldContinuationData: "id, formTemplateId, fieldTemplateId, parentFormTemplateId",
				autoSyncConfig: "id, baseTemplateId",
				projectConfig: "id, projectId",
				sentryOffline: "id, sentryEvent",
				revision: "id, fieldInstance, formGroup",
			})
			.upgrade(async (tx) => {
				// Give fieldContinuationDatas their parent formTemplateId. Give all templatey docs and users a group array
				const [fieldTemplates, options] = await Promise.all([
					tx.table("fieldTemplate").toArray() as Promise<FieldTemplateForResolver[]>,
					tx.table("option").toArray() as Promise<Option[]>,
				]);
				const defaultTemplateGroup = "PERMISSION_DefaultTemplates";
				const contDataIds = [];
				const formTemplateIds: string[] = [];
				const fieldTemplateIdToGroup: Map<string, string> = new Map();
				// Get group on fieldTemplates, get list of cont datas
				for (const fieldTemplate of fieldTemplates) {
					if (fieldTemplate.continuationDataId) {
						contDataIds.push(fieldTemplate.continuationDataId);
						formTemplateIds.push(fieldTemplate.formTemplate);
						fieldTemplateIdToGroup.set(fieldTemplate.id, fieldTemplate.group);
					}
					if (fieldTemplate.optionsOrder && fieldTemplate.optionsOrder.length > 0) fieldTemplateIdToGroup.set(fieldTemplate.id, fieldTemplate.group);
				}
				// fix options
				const finalOptions = options.map((opt) => {
					if (!opt.group) {
						const correctGroup = fieldTemplateIdToGroup.get(opt.fieldTemplateId);
						if (correctGroup) opt.group = correctGroup;
					}
					if (opt.group === "FactorEarth") opt.readGroups = [defaultTemplateGroup];
					return opt;
				});
				// Give cont data their parent formTemplate ids and fixes the group
				const contDatas: FieldContinuationDataForResolver[] = await tx.table("fieldContinuationData").bulkGet(contDataIds);
				const finalContDatas = contDatas.map((cont, index) => {
					const formTemplateId = formTemplateIds[index];
					if (!cont.parentFormTemplateId && formTemplateId) cont.parentFormTemplateId = formTemplateId;
					if (cont.group === "FactorEarth") cont.readGroups = [defaultTemplateGroup];
					return cont;
				});
				for (let i = 0; i < contDatas.length; i++) {
					const contData = contDatas[i];
					const formTemplateId = formTemplateIds[i];
					if (!contData.parentFormTemplateId && formTemplateId) contData.parentFormTemplateId = formTemplateId;
					if (contData.group === "FactorEarth") contData.readGroups = [defaultTemplateGroup];
				}
				await Promise.all([tx.table("fieldContinuationData").bulkPut(finalContDatas), tx.table("option").bulkPut(finalOptions)]);
			});
		this.version(12).stores({
			formTemplate: "id",
			fieldTemplate: "id, formTemplate, tableField", // get all field templates WHERE formTemplateId === ourFormTemplateId (the id from the formTemplateTable)
			option: "id, fieldTemplateId, formTemplate", // get all options WHERE fieldTemplateId === ourFieldTemplateId
			formGroup: "id, projectId", // get all formGroups WHERE projectId === ourProjectId
			formInstance: "id, formGroup", // get all formInstances WHERE formGroupId === ourFormGroupId
			fieldInstance: "id, formInstance, tableField, formGroup, altInstanceId, touched, [formGroup+touched]", // get all fieldInstances WHERE formInstanceId === ourFormInstanceId
			user: "id, username",
			role: "id",
			permission: "id",
			project: "id",
			image: "id, key, formGroup, project",
			imageBinary: "key",
			fieldContinuationData: "id, formTemplateId, fieldTemplateId, parentFormTemplateId",
			autoSyncConfig: "id, baseTemplateId",
			projectConfig: "id, projectId",
			sentryOffline: "id, sentryEvent",
			revision: "id, fieldInstance, formGroup",
		});
		this.version(13).stores({
			formTemplate: "id",
			fieldTemplate: "id, formTemplate, tableField", // get all field templates WHERE formTemplateId === ourFormTemplateId (the id from the formTemplateTable)
			option: "id, fieldTemplateId, formTemplate", // get all options WHERE fieldTemplateId === ourFieldTemplateId
			formGroup: "id, projectId", // get all formGroups WHERE projectId === ourProjectId
			formInstance: "id, formGroup", // get all formInstances WHERE formGroupId === ourFormGroupId
			fieldInstance: "id, formInstance, tableField, formGroup, altInstanceId, touched, [formGroup+touched]", // get all fieldInstances WHERE formInstanceId === ourFormInstanceId
			user: "id, username",
			role: "id",
			permission: "id",
			project: "id",
			image: "id, key, formGroup, project",
			imageBinary: "key",
			fieldContinuationData: "id, formTemplateId, fieldTemplateId, parentFormTemplateId",
			autoSyncConfig: "id, baseTemplateId",
			projectConfig: "id, projectId",
			sentryOffline: "id, sentryEvent",
			revision: "id, fieldInstance, formGroup",
			// Analytics event storage - minimal indexes for sync queue
			analyticsEvent: "id, offlineQueued, timestamp",
		});
	}
}

export const db = new RecordOfflineDexie();
